import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Eye, EyeOff, Lock, ArrowLeft, Mail, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${window.location.origin}/api`;
const KINESERVE_LOGO = "https://customer-assets.emergentagent.com/job_kineserve-app/artifacts/7792rvix_KineServe_Logo_Dark.svg";

export default function AdminLogin({ onLogin }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Forgot password states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, formData);
      const { token, user } = response.data;
      
      // Store token and user info
      localStorage.setItem("kineserve_token", token);
      localStorage.setItem("kineserve_user", JSON.stringify(user));
      
      toast.success(`Welcome back, ${user.name || user.email}!`);
      
      // If onLogin prop is provided, call it (when rendered from AdminPanel)
      // Otherwise, navigate to admin panel (when accessed directly)
      if (typeof onLogin === 'function') {
        onLogin(token, user);
      } else {
        navigate('/admin');
        window.location.reload();
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Login failed. Please try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);

    try {
      await axios.post(`${API}/admin/forgot-password`, { email: forgotEmail });
      setEmailSent(true);
      toast.success("Password reset instructions sent to your email.");
    } catch (error) {
      toast.error("Failed to process request. Please try again.");
    } finally {
      setForgotLoading(false);
    }
  };

  const resetForgotPasswordState = () => {
    setShowForgotPassword(false);
    setForgotEmail("");
    setEmailSent(false);
  };

  // Forgot Password View
  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="admin-forgot-password">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <img 
              src={KINESERVE_LOGO} 
              alt="KineServe" 
              className="h-10 mx-auto mb-4"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <p className="text-slate-600 mt-2">Admin Portal</p>
          </div>

          {/* Forgot Password Card */}
          <div className="bg-white border border-slate-200 p-8">
            {emailSent ? (
              // Email Sent Confirmation
              <>
                <div className="flex items-center justify-center w-16 h-16 bg-green-100 mx-auto mb-6 rounded-full">
                  <CheckCircle size={32} className="text-green-600" />
                </div>
                
                <h1 className="text-2xl font-bold text-center text-slate-900 mb-2">
                  Check Your Email
                </h1>
                <p className="text-slate-600 text-center mb-6">
                  We've sent password reset instructions to:
                </p>
                <p className="text-center font-medium text-slate-900 mb-6 bg-slate-50 py-2 px-4 rounded">
                  {forgotEmail}
                </p>
                <p className="text-sm text-slate-500 text-center mb-8">
                  If you don't see the email, check your spam folder. The link will expire in 15 minutes.
                </p>

                <Button
                  onClick={resetForgotPasswordState}
                  className="w-full bg-slate-900 text-white hover:bg-slate-800 h-12 font-medium"
                >
                  Back to Login
                </Button>
              </>
            ) : (
              // Email Input Form
              <>
                <div className="flex items-center justify-center w-12 h-12 bg-blue-100 mx-auto mb-6 rounded-full">
                  <Mail size={24} className="text-blue-600" />
                </div>
                
                <h1 className="text-2xl font-bold text-center text-slate-900 mb-2">
                  Forgot Password
                </h1>
                <p className="text-slate-600 text-center mb-8">
                  Enter your email address and we'll send you a link to reset your password.
                </p>

                <form onSubmit={handleForgotPassword} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Email Address
                    </label>
                    <Input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="admin@company.com"
                      required
                      className="h-12 bg-white"
                      data-testid="forgot-email-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={forgotLoading}
                    className="w-full bg-blue-600 text-white hover:bg-blue-700 h-12 font-medium"
                    data-testid="forgot-submit-btn"
                  >
                    {forgotLoading ? "Sending..." : "Send Reset Link"}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <button 
                    onClick={resetForgotPasswordState}
                    className="text-sm text-slate-500 hover:text-slate-700 transition-colors inline-flex items-center gap-1"
                  >
                    <ArrowLeft size={16} />
                    Back to Login
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Login View
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="admin-login">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src={KINESERVE_LOGO} 
            alt="KineServe" 
            className="h-10 mx-auto mb-4"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="hidden items-center justify-center gap-2">
            <div className="bg-slate-900 text-white px-3 py-1.5 font-bold text-lg">KS</div>
            <span className="font-bold text-xl text-slate-900">KineServe</span>
          </div>
          <p className="text-slate-600 mt-2">Admin Portal</p>
        </div>

        {/* Login Card */}
        <div className="bg-white border border-slate-200 p-8">
          <div className="flex items-center justify-center w-12 h-12 bg-slate-100 mx-auto mb-6">
            <Lock size={24} className="text-slate-600" />
          </div>
          
          <h1 className="text-2xl font-bold text-center text-slate-900 mb-2">
            Sign in to Admin
          </h1>
          <p className="text-slate-600 text-center mb-8">
            Enter your credentials to access the admin panel
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Email Address
              </label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="admin@company.com"
                required
                className="h-12 bg-white"
                data-testid="login-email-input"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-medium text-slate-700">
                  Password
                </label>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  data-testid="forgot-password-link"
                >
                  Forgot Password?
                </button>
              </div>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter your password"
                  required
                  className="h-12 pr-12 bg-white"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-slate-900 text-white hover:bg-slate-800 h-12 font-medium"
              data-testid="login-submit-btn"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <a 
              href="/" 
              className="text-sm text-slate-500 hover:text-slate-700 transition-colors"
            >
              ← Back to Website
            </a>
          </div>
        </div>

        <p className="text-center text-xs text-slate-500 mt-6">
          Protected area. Authorized personnel only.
        </p>
      </div>
    </div>
  );
}
